import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  addBeltHistory,
  deleteBeltHistory,
  editBeltHistory,
  getBeltHistoryByAdherent,
  getBeltsList,
} from "../../../../services/beltServices";

import {
  createGrade,
  deleteGrade,
  getAllGrades,
  updateGrade,
} from "../../../../services/gradCnServices";

import { getAdherentsByUser } from "../../../../services/adherentServices";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_BELT_FORM,
  DEFAULT_DAN_FORM,
  buildGradeStats,
  getLastGrade,
  getSelectedAdherent,
  normalizeDanFormFromApi,
  normalizeDanPayload,
  normalizeGradeCNFromApi,
  sortBeltHistory,
} from "../utils/passageGradeUtils";

export function usePassageGradePage() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [adherents, setAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState(null);

  const [grades, setGrades] = useState([]);
  const [belts, setBelts] = useState([]);
  const [gradeCN, setGradeCN] = useState(null);

  const [beltForm, setBeltForm] = useState(DEFAULT_BELT_FORM);
  const [gradeCNForm, setGradeCNForm] = useState(DEFAULT_DAN_FORM);
  const [selectedGrade, setSelectedGrade] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingBelt, setSavingBelt] = useState(false);
  const [savingDan, setSavingDan] = useState(false);
  const [creatingDan, setCreatingDan] = useState(false);
  const [deletingDan, setDeletingDan] = useState(false);
  const [deletingBeltIds, setDeletingBeltIds] = useState([]);

  const [isDanModalOpen, setIsDanModalOpen] = useState(false);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const selectedAdherent = useMemo(() => {
    return getSelectedAdherent(adherents, selectedAdherentId);
  }, [adherents, selectedAdherentId]);

  const lastGrade = useMemo(() => {
    return getLastGrade(grades);
  }, [grades]);

  const stats = useMemo(() => {
    return buildGradeStats(grades, lastGrade, gradeCN);
  }, [grades, lastGrade, gradeCN]);

  const fetchAdherents = useCallback(async () => {
    const response = await getAdherentsByUser();
    const safeAdherents = Array.isArray(response) ? response : [];

    setAdherents(safeAdherents);

    if (safeAdherents.length > 0) {
      setSelectedAdherentId((currentId) => currentId || safeAdherents[0].id);
    }

    return safeAdherents;
  }, []);

  const fetchAdherentGradeData = useCallback(async (adherentId) => {
    if (!adherentId) return;

    const [historyResponse, beltsResponse, gradeResponse] = await Promise.all([
      getBeltHistoryByAdherent(adherentId),
      getBeltsList(),
      getAllGrades(adherentId),
    ]);

    const safeHistory = Array.isArray(historyResponse) ? historyResponse : [];
    const safeBelts = Array.isArray(beltsResponse) ? beltsResponse : [];
    const safeGradesCN = Array.isArray(gradeResponse) ? gradeResponse : [];

    setGrades(sortBeltHistory(safeHistory));
    setBelts(safeBelts);
    setGradeCN(
      safeGradesCN.length > 0 ? normalizeGradeCNFromApi(safeGradesCN[0]) : null,
    );
  }, []);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      const isInitialLoad = !hasLoadedOnceRef.current;

      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        let currentAdherents = adherents;

        if (!currentAdherents.length) {
          currentAdherents = await fetchAdherents();
        }

        if (!currentAdherents.length) {
          setGrades([]);
          setGradeCN(null);

          notificationRef.current?.(
            "Aucun adhérent n'est rattaché à votre compte.",
            "error",
          );
          return;
        }

        const adherentId = selectedAdherentId || currentAdherents[0]?.id;

        if (!adherentId) {
          notificationRef.current?.(
            "Impossible d'identifier l'adhérent sélectionné.",
            "error",
          );
          return;
        }

        await fetchAdherentGradeData(adherentId);

        if (!silent && !isInitialLoad) {
          notificationRef.current?.(
            "Passages de grades actualisés.",
            "success",
          );
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des grades :", error);

        notificationRef.current?.(
          "Erreur lors de la récupération des passages de grades.",
          "error",
        );
      } finally {
        hasLoadedOnceRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    },
    [adherents, fetchAdherentGradeData, fetchAdherents, selectedAdherentId],
  );

  useEffect(() => {
    fetchAdherents().catch((error) => {
      console.error("Erreur lors de la récupération des adhérents :", error);
      notificationRef.current?.(
        "Erreur lors de la récupération des adhérents.",
        "error",
      );
      setLoading(false);
    });
  }, [fetchAdherents]);

  useEffect(() => {
    if (!selectedAdherentId) return;

    setSelectedGrade(null);
    setBeltForm(DEFAULT_BELT_FORM);
    setGradeCNForm(DEFAULT_DAN_FORM);
    setIsDanModalOpen(false);

    fetchData({ silent: true });
  }, [fetchData, selectedAdherentId]);

  const handleSelectAdherent = useCallback((adherentId) => {
    setSelectedAdherentId(adherentId);
  }, []);

  const handleBeltInputChange = useCallback((event) => {
    const { name, value } = event.target;

    setBeltForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSubmitBelt = useCallback(
    async (event) => {
      event.preventDefault();

      if (!selectedAdherentId) {
        notificationRef.current?.(
          "Aucun adhérent sélectionné pour ce passage de grade.",
          "error",
        );
        return;
      }

      if (!beltForm.belt_id || !beltForm.date_passage) {
        notificationRef.current?.(
          "Sélectionnez une ceinture et une date de passage.",
          "error",
        );
        return;
      }

      setSavingBelt(true);

      try {
        const payload = {
          ...beltForm,
          adherent_id: selectedAdherentId,
        };

        if (selectedGrade) {
          await editBeltHistory(selectedGrade.id, payload);
          notificationRef.current?.(
            "Passage de grade modifié avec succès.",
            "success",
          );
        } else {
          await addBeltHistory(payload);
          notificationRef.current?.(
            "Passage de grade ajouté avec succès.",
            "success",
          );
        }

        setSelectedGrade(null);
        setBeltForm(DEFAULT_BELT_FORM);

        await fetchAdherentGradeData(selectedAdherentId);
      } catch (error) {
        console.error("Erreur lors de l'enregistrement du grade :", error);

        notificationRef.current?.(
          "Erreur lors de l'enregistrement du passage de grade.",
          "error",
        );
      } finally {
        setSavingBelt(false);
      }
    },
    [beltForm, fetchAdherentGradeData, selectedAdherentId, selectedGrade],
  );

  const handleEditBelt = useCallback((grade) => {
    setSelectedGrade(grade);
    setBeltForm({
      belt_id: grade?.belt_id || "",
      date_passage: grade?.date_passage || "",
    });
  }, []);

  const handleCancelBeltEdit = useCallback(() => {
    setSelectedGrade(null);
    setBeltForm(DEFAULT_BELT_FORM);
  }, []);

  const handleDeleteBelt = useCallback(
    async (grade) => {
      if (!grade?.id || !selectedAdherentId) return;

      setDeletingBeltIds((prev) => [...prev, grade.id]);

      try {
        await deleteBeltHistory(grade.id);

        notificationRef.current?.(
          "Passage de grade supprimé avec succès.",
          "success",
        );

        await fetchAdherentGradeData(selectedAdherentId);
      } catch (error) {
        console.error("Erreur lors de la suppression du grade :", error);

        notificationRef.current?.(
          "Erreur lors de la suppression du passage de grade.",
          "error",
        );
      } finally {
        setDeletingBeltIds((prev) => prev.filter((id) => id !== grade.id));
      }
    },
    [fetchAdherentGradeData, selectedAdherentId],
  );

  const handleDeleteBeltWithConfirm = useCallback(
    async (grade) => {
      const confirmDelete = window.confirm(
        "Supprimer ce passage de grade ? Cette action retirera cette ceinture de l'historique.",
      );

      if (!confirmDelete) return;

      await handleDeleteBelt(grade);
    },
    [handleDeleteBelt],
  );

  const handleCreateDanGrade = useCallback(async () => {
    if (!selectedAdherentId) {
      notificationRef.current?.(
        "Aucun adhérent sélectionné pour créer le suivi des UV.",
        "error",
      );
      return;
    }

    setCreatingDan(true);

    try {
      const payload = {
        ...DEFAULT_DAN_FORM,
        adherent_id: selectedAdherentId,
      };

      await createGrade(payload);

      const updated = await getAllGrades(selectedAdherentId);

      setGradeCN(
        Array.isArray(updated) && updated.length
          ? normalizeGradeCNFromApi(updated[0])
          : null,
      );

      notificationRef.current?.(
        "Espace de validation des UV créé avec succès.",
        "success",
      );
    } catch (error) {
      console.error("Erreur lors de la création du suivi des UV :", error);

      notificationRef.current?.(
        "Erreur lors de la création de l'espace de validation des UV.",
        "error",
      );
    } finally {
      setCreatingDan(false);
    }
  }, [selectedAdherentId]);

  const handleOpenDanModal = useCallback(() => {
    setGradeCNForm(normalizeDanFormFromApi(gradeCN));
    setIsDanModalOpen(true);
  }, [gradeCN]);

  const handleCloseDanModal = useCallback(() => {
    setIsDanModalOpen(false);
    setGradeCNForm(DEFAULT_DAN_FORM);
  }, []);

  const handleGradeCNInputChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;

    setGradeCNForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? Boolean(checked)
          : name === "uv3_points"
            ? Number(value || 0)
            : value,
    }));
  }, []);

  const handleSaveDanGrade = useCallback(
    async (event) => {
      event.preventDefault();

      if (!selectedAdherentId) {
        notificationRef.current?.(
          "Aucun adhérent sélectionné pour enregistrer les UV.",
          "error",
        );
        return;
      }

      setSavingDan(true);

      try {
        const payload = normalizeDanPayload({
          ...gradeCNForm,
          adherent_id: selectedAdherentId,
        });

        let savedGrade = null;

        if (gradeCN?.id) {
          savedGrade = await updateGrade(gradeCN.id, payload);

          notificationRef.current?.(
            "Suivi des UV mis à jour avec succès.",
            "success",
          );
        } else {
          savedGrade = await createGrade(payload);

          notificationRef.current?.(
            "Suivi des UV créé avec succès.",
            "success",
          );
        }

        const updatedGrades = await getAllGrades(selectedAdherentId);
        const updatedGradeCN = Array.isArray(updatedGrades)
          ? updatedGrades[0]
          : savedGrade;

        setGradeCN(normalizeGradeCNFromApi(updatedGradeCN));

        handleCloseDanModal();
      } catch (error) {
        console.error("Erreur lors de l'enregistrement des UV :", error);

        notificationRef.current?.(
          "Erreur lors de l'enregistrement du suivi des UV.",
          "error",
        );
      } finally {
        setSavingDan(false);
      }
    },
    [gradeCN, gradeCNForm, handleCloseDanModal, selectedAdherentId],
  );

  const handleDeleteDanWithConfirm = useCallback(async () => {
    if (!gradeCN?.id || !selectedAdherentId) return;

    const confirmDelete = window.confirm(
      "Supprimer l'espace de validation des UV pour cet adhérent ?",
    );

    if (!confirmDelete) return;

    setDeletingDan(true);

    try {
      await deleteGrade(gradeCN.id);

      setGradeCN(null);

      notificationRef.current?.(
        "Espace de validation des UV supprimé avec succès.",
        "success",
      );
    } catch (error) {
      console.error("Erreur lors de la suppression des UV :", error);

      notificationRef.current?.(
        "Erreur lors de la suppression du suivi des UV.",
        "error",
      );
    } finally {
      setDeletingDan(false);
    }
  }, [gradeCN, selectedAdherentId]);

  return {
    adherents,
    selectedAdherentId,
    selectedAdherent,
    grades,
    belts,
    lastGrade,
    gradeCN,
    beltForm,
    gradeCNForm,
    selectedGrade,
    loading,
    refreshing,
    savingBelt,
    savingDan,
    creatingDan,
    deletingBeltIds,
    deletingDan,
    isDanModalOpen,
    stats,
    fetchData,
    handleSelectAdherent,
    handleBeltInputChange,
    handleSubmitBelt,
    handleEditBelt,
    handleCancelBeltEdit,
    handleDeleteBeltWithConfirm,
    handleCreateDanGrade,
    handleOpenDanModal,
    handleCloseDanModal,
    handleGradeCNInputChange,
    handleSaveDanGrade,
    handleDeleteDanWithConfirm,
  };
}
